import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Phone, Mail, Clock, Building2, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 4000);
    }
  };

  return (
    <section id="iletisim" data-testid="contact-section" className="py-20 sm:py-28 bg-[#F9FAFB]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <p className="overline justify-center mb-5">Bize Ulaşın</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>
            Projeniz için <span className="text-[#FF5A00]">iletişime</span> geçin
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info Cards */}
          <div className="lg:col-span-2 space-y-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#FFF0E6] flex items-center justify-center mb-3">
                <Phone size={18} className="text-[#FF5A00]" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5" style={{ fontFamily: "Outfit" }}>Telefon</p>
              <a href="tel:+905454814275" className="text-sm text-[#0C0C0C] font-medium hover:text-[#FF5A00] transition-colors">0545 481 42 75</a>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#FFF0E6] flex items-center justify-center mb-3">
                <Mail size={18} className="text-[#FF5A00]" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5" style={{ fontFamily: "Outfit" }}>E-posta</p>
              <a href="mailto:info@bctyapi.com" className="text-sm text-[#0C0C0C] font-medium hover:text-[#FF5A00] transition-colors">info@bctyapi.com</a>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#FFF0E6] flex items-center justify-center mb-3">
                <MapPin size={18} className="text-[#FF5A00]" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5" style={{ fontFamily: "Outfit" }}>Merkez Ofis</p>
              <p className="text-sm text-[#0C0C0C] font-medium whitespace-pre-line">Gültepe Mh. Eflatun Cd. ESF İşhanı{"\n"}No:26/210 Batman, Batman</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#FFF0E6] flex items-center justify-center mb-3">
                <Clock size={18} className="text-[#FF5A00]" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5" style={{ fontFamily: "Outfit" }}>Çalışma Saatleri</p>
              <p className="text-sm text-[#0C0C0C] font-medium">Pzt - Cuma: 09:00 - 18:00{"\n"}Cmt: 09:00 - 14:00</p>
            </motion.div>
          </div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-3 bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Ad Soyad</label>
                  <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="contact-input" placeholder="Adınız Soyadınız" data-testid="contact-name" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>E-posta</label>
                  <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="contact-input" placeholder="ornek@email.com" data-testid="contact-email" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Telefon</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="contact-input" placeholder="+90 (___) ___ __ __" data-testid="contact-phone" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2" style={{ fontFamily: "Outfit" }}>Mesajınız</label>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="contact-input resize-none" placeholder="Projeniz hakkında bilgi verin..." data-testid="contact-message" />
              </div>
              <div className="flex items-center gap-4 pt-2">
                <button type="submit" disabled={loading} className="btn-modern btn-filled disabled:opacity-50" data-testid="contact-submit">
                  <Send size={15} /> {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
                </button>
                {status === "success" && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-emerald-600 font-medium">Mesajınız gönderildi!</motion.span>}
                {status === "error" && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-red-500 font-medium">Hata oluştu, tekrar deneyin.</motion.span>}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
